import { FC, Fragment } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Tag } from 'antd';
import { capitalCase } from 'change-case';
import { TbArrowRight, TbBuilding, TbLink } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { normalizeURL, withoutTrailingSlash } from 'ufo';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { VirtualizedList as List, MiddleDot } from '@/shared';
import { CompanySearchResult } from '@/shared/components/company-search/utils';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useInvestmentStore } from '@/stores';
import { Investment, InvestmentVisibilityType, SetStateMethod } from '@/types';
import { corporateLogo, ellipsisStyling } from '@/utilities';

interface AssetListItemProps {
    asset: CompanySearchResult;
    createMore?: boolean;
    creatingAssetKey?: string;
    setOpen: SetStateMethod<boolean>;
    setInput: SetStateMethod<string>;
    visibility: InvestmentVisibilityType;
    onCreated?: () => void;
    onBeginCreatingAsset: (assetKey: string) => boolean;
    onFinishCreatingAsset: (assetKey: string) => void;
}

const AssetListItem: FC<AssetListItemProps> = ({
    asset,
    createMore,
    creatingAssetKey,
    setOpen,
    setInput,
    visibility,
    onCreated,
    onBeginCreatingAsset,
    onFinishCreatingAsset,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { createInvestment, investments } = useInvestmentStore();
    const createAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);

    const navigate = useNavigate();
    const assetKey = String(asset.entityId ?? asset.symbol ?? asset.enrichment?.cik ?? asset.name);
    const creating = creatingAssetKey === assetKey;
    const creationPending = Boolean(creatingAssetKey);

    const handleCreateInvestment = async () => {
        if (createAccess.disabled || creationPending || !onBeginCreatingAsset(assetKey)) {
            return;
        }

        try {
            const headquarters =
                asset?.enrichment?.country === 'US' ? asset?.enrichment?.city : asset?.enrichment?.country;

            const address =
                asset?.enrichment?.address && asset?.enrichment?.address2
                    ? `${asset?.enrichment?.address}, ${asset?.enrichment?.address2}`
                    : asset?.enrichment?.address
                      ? asset?.enrichment?.address
                      : '-';

            const assetPayload = {
                name: asset?.enrichment?.companyName,
                editedName: asset?.enrichment?.companyName,
                description: asset?.enrichment?.description,
                tickerSymbol: asset?.symbol,
                marketType: asset?.enrichment?.clearbit?.type
                    ? capitalCase(asset?.enrichment?.clearbit?.type)
                    : 'Public',
                financingType: 'Equity',
                issuance: asset?.enrichment?.issueType,
                cik: asset?.enrichment?.cik,
                lei: asset?.enrichment?.lei, // ?
                figi: asset?.enrichment?.figi, // ?
                exchange: asset.enrichment?.exchange ?? asset.exchange,
                foundingDate: asset?.enrichment?.clearbit?.foundedYear
                    ? String(asset?.enrichment?.clearbit?.foundedYear)
                    : '-',
                ipoDate: asset?.enrichment?.ipoDate ? String(asset?.enrichment?.ipoDate) : '-',
                address,
                headquarters,
                country: asset?.enrichment?.country,
                industry: asset?.enrichment?.industry
                    ? asset?.enrichment?.industry
                    : asset?.enrichment?.clearbit?.category?.industry,
                sector: asset?.enrichment?.sector,
                ceo: asset?.enrichment?.ceo,
                employees: asset?.enrichment?.fullTimeEmployees,
                website: asset?.enrichment?.website
                    ? normalizeURL(withoutTrailingSlash(asset.enrichment.website, true))
                    : undefined,
                tags: asset?.enrichment?.clearbit?.tags,
                type: 'Personal',
                visibility,
                clearbit: {
                    ...asset?.enrichment?.clearbit,
                    fmp: {
                        exchange: asset.enrichment?.exchange ?? asset.exchange,
                        symbol: asset.symbol,
                    },
                },
            } as Partial<Investment>;

            const investment = await createInvestment(assetPayload);

            if (!investment?.id) {
                throw new Error('Investment creation did not return an investment.');
            }

            onCreated?.();
            setInput('');

            if (!createMore) {
                navigate(`/investments/${investment.id}`);
                setOpen(false);
            }
        } catch (error) {
            captureException(error);
            toast.error('Unable to create investment');
        } finally {
            onFinishCreatingAsset(assetKey);
        }
    };

    return (
        <List.Item
            actions={[
                <Fragment key='removeInvestment'>
                    <Button
                        type='dashed'
                        disabled={creationPending || createAccess.disabled}
                        loading={creating}
                        onClick={() => void handleCreateInvestment()}
                    >
                        Create
                        <TbArrowRight size='1rem' style={{ color: token.colorTextTertiary }} />
                    </Button>
                </Fragment>,
            ]}
        >
            <List.Item.Meta
                avatar={
                    <div style={{ marginTop: '0.25rem' }}>
                        <Avatar
                            // src={data?.logo !== '' ? data?.logo : undefined}
                            src={asset?.enrichment?.website && corporateLogo(asset?.enrichment?.website)}
                            icon={<TbBuilding style={{ verticalAlign: '-0.175rem', color: token.colorTextTertiary }} />}
                            size={'large'}
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                            }}
                            shape='square'
                        />
                    </div>
                }
                title={
                    <div style={{ maxWidth: '50rem' }}>
                        {asset?.name}
                        {asset?.enrichment?.exchange && asset?.symbol && (
                            <Tag style={{ borderColor: 'transparent', marginLeft: '0.5rem' }}>
                                <strong>{asset?.enrichment?.exchange}:</strong>{' '}
                                <span style={{ fontWeight: 'normal' }}>{asset?.symbol}</span>
                            </Tag>
                        )}
                        {/* <span style={{ fontWeight: 'normal' }}>
                                    {iexSecurtyTypeShorthandConverter(asset?.securityType)}
                                </span> */}
                    </div>
                }
                description={
                    <div style={{ maxWidth: '50rem', ...ellipsisStyling }}>
                        <span style={{ verticalAlign: '-0.125rem' }}>
                            {asset?.enrichment?.website && (
                                <a
                                    href={asset?.enrichment?.website}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{ color: 'inherit' }}
                                >
                                    <TbLink size='0.875rem' style={{ marginRight: '0.5rem' }} />
                                </a>
                            )}
                            {/* {asset?.enrichment?.clearbit?.linkedin?.handle && (
                                                            <a
                                                                href={`https://linkedin.com/${asset?.enrichment?.clearbit?.linkedin?.handle}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: 'inherit' }}
                                                            >
                                                                <BsLinkedin
                                                                    size="0.875rem"
                                                                    style={{
                                                                        marginRight: '0.5rem',
                                                                        borderRadius: token.borderRadius,
                                                                    }}
                                                                />
                                                            </a>
                                                        )}
                                                        {asset?.enrichment?.clearbit?.twitter?.handle && (
                                                            <a
                                                                href={`http://x.com/${asset?.enrichment?.clearbit?.twitter?.handle}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: 'inherit' }}
                                                            >
                                                                <FaSquareXTwitter
                                                                    size="1rem"
                                                                    style={{
                                                                        marginRight: '0.5rem',
                                                                        verticalAlign: '-0.05rem',
                                                                    }}
                                                                />
                                                            </a>
                                                        )}
                                                        {asset?.enrichment?.clearbit?.crunchbase?.handle && (
                                                            <a
                                                                href={`https://crunchbase.com/${asset?.enrichment?.clearbit?.crunchbase?.handle}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: 'inherit' }}
                                                            >
                                                                <SiCrunchbase
                                                                    size="0.875rem"
                                                                    style={{
                                                                        marginRight: '0.5rem',
                                                                        borderRadius: token.borderRadius,
                                                                    }}
                                                                />
                                                            </a>
                                                        )}
                                                        {asset?.enrichment?.clearbit?.facebook?.handle && (
                                                            <a
                                                                href={`https://facebook.com/${asset?.enrichment?.clearbit?.facebook?.handle}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: 'inherit' }}
                                                            >
                                                                <FaFacebook size="0.875rem" />
                                                            </a>
                                                        )} */}
                        </span>
                        {(asset?.enrichment?.website ||
                            asset?.enrichment?.clearbit?.linkedin?.handle ||
                            asset?.enrichment?.clearbit?.twitter?.handle ||
                            asset?.enrichment?.clearbit?.crunchbase?.handle ||
                            asset?.enrichment?.clearbit?.facebook?.handle) && (
                            <MiddleDot styles={{ marginRight: '0.5rem', marginLeft: 0 }} />
                        )}
                        <span style={{ ...ellipsisStyling }}>
                            {asset?.enrichment?.sector ? asset?.enrichment?.sector : 'Other'}
                        </span>
                    </div>
                }
            />
        </List.Item>
    );
};

export default AssetListItem;
